/**
 * MB Scratch & Win Popup — 2026
 *
 * ScratchCard: Canvas-based interactive scratch layer.
 * MBScratchPopup: Custom element extending DrawerComponent.
 *
 * Flow: scratch card → email capture → success
 * Triggers: exit-intent (desktop), 60% scroll (mobile), 8s delay
 * Storage: localStorage key 'mb:scratch-popup'
 * Signup: Klaviyo client subscription API (public endpoint, no Shopify /contact)
 */

/* Klaviyo — MyBouddha public company id + the list new signups are added to */
const MB_KLAVIYO_COMPANY_ID = 'LBfXbs';
const MB_KLAVIYO_LIST_ID    = 'UzELGL';
const MB_KLAVIYO_REVISION   = '2025-04-15';

/* =============================================================
   ScratchCard — canvas scratch engine
   ============================================================= */

class ScratchCard {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {object} options
   *   color      — fill color for the scratch layer (hex/rgb)
   *   radius     — brush radius in CSS px
   *   threshold  — fraction revealed to auto-complete (0–1)
   *   onReveal   — called once when threshold is crossed
   *   onFirst    — called on first scratch gesture
   */
  constructor(canvas, { color = '#231a0e', radius = 32, threshold = 0.45, onReveal = () => {}, onFirst = () => {} } = {}) {
    this.canvas    = canvas;
    this.ctx       = canvas.getContext('2d');
    this.color     = color;
    this.radius    = radius;
    this.threshold = threshold;
    this.onReveal  = onReveal;
    this.onFirst   = onFirst;

    this.scratching  = false;
    this.revealed    = false;
    this.started     = false;
    this._raf        = null;
    this._checkScheduled = false;

    this._init();
  }

  _init() {
    this._size();
    this._draw();
    this._bind();
  }

  /* Set canvas pixel dimensions matching CSS size, accounting for DPR */
  _size() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr  = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width  = Math.round(rect.width  * dpr);
    this.canvas.height = Math.round(rect.height * dpr);
    this.ctx.scale(dpr, dpr);
    this._w = rect.width;
    this._h = rect.height;
  }

  /* Draw a warm-gold metallic scratch surface */
  _draw() {
    const { ctx, _w: w, _h: h } = this;

    // Brushed-metal gold base — diagonal gradient for depth
    const base = ctx.createLinearGradient(0, 0, w, h);
    base.addColorStop(0,    '#C9A55B'); // light gold
    base.addColorStop(0.45, '#A07F2D'); // mid amber
    base.addColorStop(0.55, '#8C6E22'); // shadow band
    base.addColorStop(1,    '#B8902E'); // deep amber
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);

    // Specular highlight — soft horizontal sheen
    const sheen = ctx.createLinearGradient(0, 0, 0, h);
    sheen.addColorStop(0,   'rgba(255, 245, 215, 0.28)');
    sheen.addColorStop(0.5, 'rgba(255, 245, 215, 0)');
    sheen.addColorStop(1,   'rgba(0, 0, 0, 0.12)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, w, h);

    // Brushed grain — fine vertical lines
    ctx.globalAlpha = 0.06;
    for (let x = 0; x < w; x += 2) {
      ctx.fillStyle = (x % 4 === 0) ? '#fff8e0' : '#3a2a08';
      ctx.fillRect(x, 0, 1, h);
    }
    ctx.globalAlpha = 1;

    // Soft corner vignette for premium feel
    const vignette = ctx.createRadialGradient(w / 2, h / 2, h * 0.2, w / 2, h / 2, h * 0.95);
    vignette.addColorStop(0, 'rgba(0,0,0,0)');
    vignette.addColorStop(1, 'rgba(50,30,5,0.32)');
    ctx.fillStyle = vignette;
    ctx.fillRect(0, 0, w, h);
  }

  _bind() {
    const el = this.canvas;

    // Desktop: scratch on hover (no click needed) — feels way more responsive.
    el.addEventListener('mousemove',  e => this._scratch(e));
    el.addEventListener('mouseleave', () => { this._lastX = this._lastY = null; });

    // Touch: drag-based (no other choice on mobile).
    el.addEventListener('touchstart', e => { this._lastX = this._lastY = null; this._scratch(e); }, { passive: true });
    el.addEventListener('touchmove',  e => { e.preventDefault(); this._scratch(e); }, { passive: false });
    el.addEventListener('touchend',   () => { this._lastX = this._lastY = null; });
  }

  _xy(e) {
    const rect = this.canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  }

  _scratch(e) {
    if (this.revealed) return;

    if (!this.started) {
      this.started = true;
      this.onFirst();
    }

    const { ctx } = this;
    const { x, y } = this._xy(e);

    ctx.globalCompositeOperation = 'destination-out';

    // Connect last position with a thick line so fast mouse movement doesn't
    // leave gaps. Falls back to a single circle on the first event of a stroke.
    if (this._lastX != null && this._lastY != null) {
      ctx.lineWidth = this.radius * 2;
      ctx.lineCap   = 'round';
      ctx.lineJoin  = 'round';
      ctx.beginPath();
      ctx.moveTo(this._lastX, this._lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(x, y, this.radius, 0, Math.PI * 2);
      ctx.fill();
    }

    this._lastX = x;
    this._lastY = y;

    // Throttle pixel check to once per animation frame
    if (!this._checkScheduled) {
      this._checkScheduled = true;
      requestAnimationFrame(() => {
        this._checkScheduled = false;
        this._checkThreshold();
      });
    }
  }

  /* Sample 1/16 of pixels for performance */
  _checkThreshold() {
    if (this.revealed) return;
    const { ctx, canvas } = this;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let clear = 0, total = 0;
    for (let i = 3; i < data.length; i += 64) { // every 16th pixel
      if (data[i] < 128) clear++;
      total++;
    }
    if (total > 0 && clear / total >= this.threshold) {
      this.revealAll();
    }
  }

  /* Fade the canvas element via CSS opacity then remove it from DOM.
     Bulletproof — once removed, can't leave any residue. */
  revealAll() {
    if (this.revealed) return;
    this.revealed = true;

    const { canvas } = this;
    canvas.style.transition = 'opacity 0.32s cubic-bezier(0.16, 1, 0.3, 1)';
    canvas.style.opacity    = '0';
    canvas.style.pointerEvents = 'none';

    setTimeout(() => {
      canvas.remove();
      this.onReveal();
    }, 320);
  }
}

/* =============================================================
   MBScratchPopup — custom element
   ============================================================= */

customElements.whenDefined('drawer-component').then(() => {
const DrawerComponent = customElements.get('drawer-component');
if (!customElements.get('mb-scratch-popup')) {
  customElements.define(
    'mb-scratch-popup',
    class MBScratchPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      get requiresBodyAppended() { return false; }

      connectedCallback() {
        super.connectedCallback();
        this.COOKIE      = 'mb:scratch-popup';
        this.triggerOpen   = this.dataset.triggerOpen || 'exit_intent';
        this.repeatOpen    = this.dataset.repeatOpen  || 'no_repeat';
        // Custom delay (seconds) when trigger=delay; defaults to 8s if missing
        this.delaySeconds  = parseInt(this.dataset.delaySeconds, 10);
        if (!Number.isFinite(this.delaySeconds) || this.delaySeconds < 1) this.delaySeconds = 8;
        this._prize      = null;
        this._scratcher  = null;
        this._exitHandler   = null;
        this._scrollHandler = null;

        // ?mb_popup=1 force-show — runs IMMEDIATELY, doesn't wait for FoxTheme/DOMReady
        // so it works even if theme JS bootstrap fails on mobile.
        const force = new URLSearchParams(location.search).get('mb_popup') === '1';
        console.log('[mb-popup] connected, force=', force, 'FoxTheme=', !!window.FoxTheme);

        const init = () => {
          try { this._selectPrize(); } catch (e) { console.warn('[mb-popup] selectPrize err', e); }
          try { this._bindSteps();   } catch (e) { console.warn('[mb-popup] bindSteps err', e); }
          try { this._initTrigger(); } catch (e) { console.warn('[mb-popup] initTrigger err', e); }
        };

        if (window.FoxTheme?.DOMready) {
          window.FoxTheme.DOMready(init);
        } else {
          // Fallback: FoxTheme not available — init on DOMContentLoaded directly
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init, { once: true });
          } else {
            init();
          }
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback?.();
        if (this._exitHandler)   document.removeEventListener('mouseleave', this._exitHandler);
        if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler, { passive: true });
      }

      // ─── Prize selection ─────────────────────────────────────

      _selectPrize() {
        try {
          const raw    = this.querySelector('[data-prizes-config]')?.textContent || '[]';
          const prizes = JSON.parse(raw).filter(p => p.label);
          if (!prizes.length) return;

          // ?mb_popup=1 testing flag: force the prize that has a discount code
          // so QA can always see the code-box flow (otherwise random odds).
          const force = new URLSearchParams(location.search).get('mb_popup') === '1';
          if (force) {
            const withCode = prizes.find(p => p.code);
            if (withCode) {
              this._prize = withCode;
            }
          }

          // Weighted random selection (only if not force-selected above)
          if (!this._prize) {
            const totalWeight = prizes.reduce((sum, p) => sum + (p.weight || 1), 0);
            let roll = Math.random() * totalWeight;
            for (const p of prizes) {
              roll -= (p.weight || 1);
              if (roll <= 0) { this._prize = p; break; }
            }
            if (!this._prize) this._prize = prizes[0];
          }

          // Update prize display in step 1
          this._updatePrizeDisplay('[data-prize-emoji]',  this._prize.emoji);
          this._updatePrizeDisplay('[data-prize-label]',  this._prize.label);
          this._updatePrizeDisplay('[data-prize-sub]',    this._prize.sub);
          // Update step 2
          this._updatePrizeDisplay('[data-step2-emoji]',  this._prize.emoji);
          this._updatePrizeDisplay('[data-step2-prize]',  this._prize.label);
          // Update prize tag input
          const tagInput = this.querySelector('[data-prize-tag-input]');
          if (tagInput && this._prize.label) {
            tagInput.value = 'prize:' + this._prize.label.toLowerCase().replace(/\s+/g, '-');
          }
        } catch (e) {
          // silently fail — default values from Liquid are already in the DOM
        }
      }

      _updatePrizeDisplay(selector, value) {
        if (!value) return;
        const el = this.querySelector(selector);
        if (el) el.textContent = value;
      }

      // ─── Step navigation ─────────────────────────────────────

      _bindSteps() {
        // Scratch CTA → step 2
        this.querySelector('[data-scratch-cta]')?.addEventListener('click', () => this._goToStep(2));

        // Back buttons
        this.querySelectorAll('[data-go-step]').forEach(btn => {
          btn.addEventListener('click', () => this._goToStep(parseInt(btn.dataset.goStep, 10)));
        });

        // Form submit — bulletproof: capture phase + immediate-stop so no other
        // listener can race ahead and trigger native form submission/page reload.
        const form = this.querySelector('form');
        if (form) {
          // Capture phase: runs BEFORE any listener attached at the form level
          form.addEventListener('submit', e => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this._handleSubmit(e);
          }, true);
          // Also override the onsubmit attribute path in case theme JS sets it
          form.onsubmit = (e) => { e.preventDefault(); return false; };
        }

        // Click-to-copy for the discount code box
        this.querySelector('[data-code-copy]')?.addEventListener('click', e => {
          e.preventDefault();
          this._copyCode();
        });
      }

      _copyCode() {
        const valEl     = this.querySelector('[data-code-value]');
        const hintEl    = this.querySelector('[data-code-copy-hint]');
        const copiedEl  = this.querySelector('[data-code-copied]');
        const code      = valEl?.textContent?.trim();
        if (!code) return;

        const showFeedback = () => {
          hintEl?.setAttribute('hidden', '');
          copiedEl?.removeAttribute('hidden');
          setTimeout(() => {
            copiedEl?.setAttribute('hidden', '');
            hintEl?.removeAttribute('hidden');
          }, 2000);
        };

        // Try modern clipboard API first; fall back to a hidden textarea + execCommand
        if (navigator.clipboard?.writeText) {
          navigator.clipboard.writeText(code).then(showFeedback).catch(() => {
            this._fallbackCopy(code, showFeedback);
          });
        } else {
          this._fallbackCopy(code, showFeedback);
        }
      }

      _fallbackCopy(text, onDone) {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity  = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); onDone(); } catch (_) {}
        document.body.removeChild(ta);
      }

      _goToStep(n) {
        const track = this.querySelector('.mb-scratch__track');
        if (track) track.dataset.currentStep = n;

        // Accessibility
        this.querySelectorAll('[data-step]').forEach(step => {
          step.setAttribute('aria-hidden', step.dataset.step !== String(n) ? 'true' : 'false');
        });

        // Focus email input on step 2
        if (n === 2) {
          setTimeout(() => this.querySelector('#MBScratchEmail')?.focus(), 280);
        }
      }

      // ─── Canvas init (after popup is visible) ────────────────

      prepareToShow() {
        super.prepareToShow();
        this._setRepeatOpen();
        // Canvas must be sized after layout is painted
        setTimeout(() => this._initCanvas(), 120);
        // Pre-emptively remove the theme's footer newsletter alert modal so it
        // can never appear over our popup at any step in the flow.
        document.querySelectorAll('.modal--newsletter-alert').forEach(m => m.remove());
      }

      _initCanvas() {
        const canvas = this.querySelector('[data-scratch-canvas]');
        if (!canvas || this._scratcher) return;

        const color = this.dataset.scratchColor || '#231a0e';

        this._scratcher = new ScratchCard(canvas, {
          color,
          radius:    34,
          threshold: 0.45,
          onFirst: () => {
            // Fade out the hint overlay on first scratch
            const hint = this.querySelector('[data-scratch-hint]');
            hint?.classList.add('mb-scratch__hint--faded');
          },
          onReveal: () => {
            // Trigger prize pop animation
            this.querySelector('[data-prize-area]')?.classList.add('mb-scratch__prize--revealed');
            // Show CTA
            const cta = this.querySelector('[data-scratch-cta]');
            if (cta) cta.removeAttribute('hidden');
          },
        });
      }

      // ─── Form submission ─────────────────────────────────────

      async _handleSubmit(e) {
        e.preventDefault();
        const form    = e.target;

        // Bail early if form fails native validation — don't show spinner
        // for empty/invalid email
        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        const btn     = form.querySelector('.mb-scratch__submit');
        const label   = btn?.querySelector('.mb-scratch__submit-label');
        const spinner = btn?.querySelector('.mb-scratch__submit-spinner');
        const errorEl = form.querySelector('[data-form-error]');

        if (label)   label.style.opacity = '0';
        if (spinner) spinner.removeAttribute('hidden');
        if (btn)     btn.disabled = true;
        if (errorEl) errorEl.classList.add('visually-hidden');

        // Subscribe through Klaviyo's public client API (JSON:API body). Any 2xx
        // (Klaviyo answers 202) is success; non-2xx or a network error is 'error'.
        // Race against a timeout so the spinner can't hang forever.
        const email = String(new FormData(form).get('contact[email]') || '').trim();
        const prize = this._prize?.label
          || this.querySelector('[data-step2-prize]')?.textContent?.trim()
          || '';

        const submission = fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${MB_KLAVIYO_COMPANY_ID}`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', 'revision': MB_KLAVIYO_REVISION },
          body:    JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'MB scratch popup',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      properties: { 'MB scratch prize': prize, 'MB signup page': location.pathname },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: MB_KLAVIYO_LIST_ID } } },
            },
          }),
        }).then(r => (r.ok ? 'ok' : 'error')).catch(() => 'error');

        const timeout = new Promise(r => setTimeout(() => r('timeout'), 5000));
        const result  = await Promise.race([submission, timeout]);

        if (result === 'error') {
          if (label)   label.style.opacity = '';
          if (spinner) spinner.setAttribute('hidden', '');
          if (btn)     btn.disabled = false;
          if (errorEl) {
            errorEl.textContent = 'Une erreur est survenue. Réessaie.';
            errorEl.classList.remove('visually-hidden');
          }
          return;
        }

        // 'ok' or 'timeout' — both treated as success (request was sent, Klaviyo
        // will process it even if we didn't see the response within 5s)
        this._onEmailSuccess();
      }

      _onEmailSuccess() {
        // Show discount code if prize has one
        if (this._prize?.code) {
          const box = this.querySelector('[data-success-code-box]');
          const val = this.querySelector('[data-code-value]');
          if (box && val) {
            val.textContent = this._prize.code;
            box.removeAttribute('hidden');
          }
        }

        // Suppress the theme's footer newsletter alert modal ("Merci de vous
        // être abonné"). The modal already exists in DOM with [hidden]; the
        // theme just toggles attributes to show it. So we both remove it AND
        // watch for any attempt to re-show it.
        window.isNewsletterModalShow = true;

        const killAlerts = () => {
          document.querySelectorAll('.modal--newsletter-alert').forEach(m => m.remove());
        };
        killAlerts();

        // Watch for attribute mutations too (theme uses [active]/[hidden] toggle)
        // AND child additions in case theme re-injects the modal later.
        const obs = new MutationObserver(() => killAlerts());
        obs.observe(document.body, {
          childList:        true,
          subtree:          true,
          attributes:       true,
          attributeFilter:  ['active', 'hidden', 'class'],
        });
        setTimeout(() => obs.disconnect(), 30000);

        this._trackSuccess();
        this._goToStep(3);
      }

      // ─── Trigger logic ────────────────────────────────────────

      _initTrigger() {
        // In the theme editor (Shopify customizer) we DON'T auto-show — that
        // matches the rest of the theme's popup pattern (subscription-popup.js).
        // Merchants can preview the popup by clicking the section in the sidebar,
        // which fires shopify:section:select and triggers show() via the parent
        // DrawerComponent.
        if (Shopify.designMode) return;

        // Dev/test escape hatch: ?mb_popup=1 forces the popup to show immediately,
        // bypassing localStorage and trigger conditions. Useful for QA — has no
        // effect in normal traffic.
        if (new URLSearchParams(location.search).get('mb_popup') === '1') {
          setTimeout(() => {
            // Try the proper drawer show() first (handles body-scroll lock, focus, etc.)
            try { this.show(); } catch (_) {}
            // Failsafe: also force-set the visibility attributes directly in case
            // theme JS internals behave differently on mobile / fail silently
            this.removeAttribute('hidden');
            this.setAttribute('active', '');
            document.body.classList.add('modal-show');
            // Init the canvas if prepareToShow didn't run
            if (!this._scratcher && typeof this._initCanvas === 'function') {
              setTimeout(() => this._initCanvas(), 50);
            }
            console.log('[mb-popup] forced via ?mb_popup=1');
          }, 200);
          return;
        }

        const saved = FoxTheme.utils.getStorage(this.COOKIE);
        if (saved?.opened) return;

        const mobile = window.matchMedia('(max-width: 767px)').matches;

        if (this.triggerOpen === 'exit_intent' && !mobile) {
          this._setupExitIntent();
        } else if (this.triggerOpen === 'exit_intent' || this.triggerOpen === 'scroll_60') {
          this._setupScrollTrigger(0.6);
        } else {
          // 'delay' trigger — use the merchant-configured delay_seconds
          setTimeout(() => this.show(), this.delaySeconds * 1000);
        }
      }

      _setupExitIntent() {
        let triggered = false;
        this._exitHandler = e => {
          if (triggered || e.clientY > 0) return;
          triggered = true;
          document.removeEventListener('mouseleave', this._exitHandler);
          this._exitHandler = null;
          this.show();
        };
        document.addEventListener('mouseleave', this._exitHandler);
      }

      _setupScrollTrigger(threshold = 0.6) {
        let triggered = false;
        this._scrollHandler = () => {
          if (triggered) return;
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable > 0 && window.scrollY / scrollable >= threshold) {
            triggered = true;
            window.removeEventListener('scroll', this._scrollHandler, { passive: true });
            this._scrollHandler = null;
            this.show();
          }
        };
        window.addEventListener('scroll', this._scrollHandler, { passive: true });
      }

      // ─── Storage ─────────────────────────────────────────────

      _setRepeatOpen() {
        if (Shopify.designMode) return;
        const days = { no_repeat: 365, every_week: 7, every_2_weeks: 14, every_month: 30 }[this.repeatOpen] ?? 365;
        FoxTheme.utils.setStorage(this.COOKIE, { opened: true }, days);
      }

      _trackSuccess() {
        FoxTheme.utils.setStorage(this.COOKIE, { opened: true, subscribed: true }, 3650);
      }
    }
  );
}
});
